import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import axios from 'axios';
import type { TTSVoice, TTSRequest } from '../types';
import { getVoiceName, generatePreviewMessage } from '../utils/voiceUtils';
import { serializeError } from '../utils/apiUtils';
import { azureConfig } from '../config/azure';

// Consolidated Azure configuration from config file
const AZURE_CONFIG = {
  key: azureConfig.subscriptionKey,
  backupKey: azureConfig.backupKey, // Added backup key for failover
  region: azureConfig.region,
  endpoint: 'https://eastus.tts.speech.microsoft.com/' // Using TTS-specific endpoint
};

// Cache for frequently used audio segments (simple in-memory implementation)
const audioCache = new Map<string, string>();
const CACHE_MAX_SIZE = 100; // Limit cache size to prevent memory issues

const TIMEOUT_MS = 30000; // 30 seconds
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

/**
 * Helper function to enhance text with better pausing and emphasis for more natural speech
 * This is a simple implementation that can be expanded based on specific needs
 */
const addPausingAndEmphasis = (text: string): string => {
  if (!text) return '';
  
  // First, safely encode text to prevent XML/SSML injection
  let safeText = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
  
  // Then add natural pauses after punctuation
  // Important: We need to encode the break tags properly to avoid SSML parsing errors
  // Use XML entity references for break tags to prevent them from being interpreted as XML
  safeText = safeText
    .replace(/\. /g, '. ')
    .replace(/\! /g, '! ')
    .replace(/\? /g, '? ')
    .replace(/; /g, '; ')
    .replace(/: /g, ': ')
    .replace(/, /g, ', ');
    
  return safeText;
};

const retryWithDelay = async (fn: () => Promise<any>, retries: number, delay: number): Promise<any> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithDelay(fn, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const fetchAzureVoices = async (): Promise<TTSVoice[]> => {
  const fetchVoices = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const response = await axios.get(
        `${AZURE_CONFIG.endpoint}cognitiveservices/voices/list`,
        {
          headers: {
            'Ocp-Apim-Subscription-Key': AZURE_CONFIG.key
          },
          signal: controller.signal,
          timeout: TIMEOUT_MS
        }
      );

      clearTimeout(timeout);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error('Invalid response format from Azure API');
      }

      return response.data
        .filter((voice: any) => {
          // Validate required voice properties
          if (!voice || !voice.ShortName || !voice.Gender || !voice.Locale) {
            console.warn('Skipping invalid voice:', voice);
            return false;
          }
          // Prioritize Neural voices that are Generally Available
          return voice.Status === 'GA' && voice.ShortName.toLowerCase().includes('neural');
        })
        .map((voice: any) => {
          try {
            const gender = voice.Gender.toLowerCase();
            const name = getVoiceName(voice.ShortName, gender);
            const previewText = generatePreviewMessage(name, voice.Locale);
            const styles = voice.StyleList?.map((s: string) => s.toLowerCase()) || [];

            return {
              id: voice.ShortName,
              name,
              gender,
              language: voice.Locale,
              accent: voice.LocaleName,
              // Ensure type is one of the allowed values in TTSVoice interface
              type: 'Neural2' as const,
              quality: 'hd' as const,
              provider: 'azure' as const,
              style: styles[0] || 'general',
              styles,
              previewText,
              tags: [
                'neural',
                gender,
                voice.Locale,
                'azure',
                'hd',
                ...(styles || [])
              ],
              supportsSpeed: true,
              supportsPitch: true,
              naturalSampleRateHertz: parseInt(voice.SampleRateHertz, 10)
            };
          } catch (error) {
            console.warn('Failed to process voice:', voice, error);
            return null;
          }
        })
        .filter((voice: TTSVoice | null): voice is TTSVoice => voice !== null);
    } catch (error: any) {
      clearTimeout(timeout);
      throw error;
    }
  };

  try {
    return await retryWithDelay(fetchVoices, MAX_RETRIES, RETRY_DELAY);
  } catch (error: any) {
    console.error('Failed to fetch Azure voices:', serializeError(error));
    throw new Error(error.message || 'Failed to fetch Azure voices');
  }
};

export const synthesizeAzureSpeech = async (request: TTSRequest): Promise<string> => {
  // Check cache first for performance optimization
  const cacheKey = `${request.voice.id}_${request.text}_${request.settings.speed}_${request.settings.pitch}_${request.settings.volume}_${request.settings.style || 'default'}`;
  if (audioCache.has(cacheKey)) {
    console.log('Using cached audio for:', request.voice.id);
    const cachedAudio = audioCache.get(cacheKey);
    if (cachedAudio) {
      return Promise.resolve(cachedAudio);
    }
  }
  
  return new Promise((resolve, reject) => {
    try {
      const speechConfig = sdk.SpeechConfig.fromSubscription(AZURE_CONFIG.key, AZURE_CONFIG.region);
      speechConfig.speechSynthesisVoiceName = request.voice.id;
      // Using highest quality audio format (48kHz/192kbps) for premium voice quality
      speechConfig.speechSynthesisOutputFormat = 
        sdk.SpeechSynthesisOutputFormat.Audio48Khz192KBitRateMonoMp3;
      
      // Important: Create an audio output config that prevents auto-playback
      // By default, the SDK would use the default speaker for output
      // We'll create a custom AudioConfig that doesn't play audio
      
      // Create a custom AudioOutputStream that doesn't play audio
      const audioOutputStream = sdk.AudioOutputStream.createPullStream();
      const audioConfig = sdk.AudioConfig.fromStreamOutput(audioOutputStream);
      
      // Create a synthesizer with our custom audio config that doesn't play audio
      const synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioConfig);

      const timeoutId = setTimeout(() => {
        synthesizer.close();
        reject(new Error('Speech synthesis timed out'));
      }, TIMEOUT_MS);

      // Enhanced SSML template with better style control and natural pausing
      // Fix: Ensure all values are properly sanitized and formatted
      const sanitizedVoiceId = request.voice.id.replace(/"/g, '&quot;');
      const sanitizedLanguage = request.voice.language.replace(/"/g, '&quot;');
      const sanitizedStyle = request.settings.style ? request.settings.style.replace(/"/g, '&quot;') : '';
      
      // Format prosody values properly
      const rate = `${Math.max(-50, Math.min(50, Math.round((request.settings.speed - 1) * 100)))}%`;
      const pitch = `${Math.max(-50, Math.min(50, Math.round(request.settings.pitch)))}%`;
      const volume = `${Math.max(-50, Math.min(50, Math.round(request.settings.volume)))}%`;
      
      // Build SSML with proper formatting and structure
      const ssml = `
<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="${sanitizedLanguage}">
  <voice name="${sanitizedVoiceId}">
    <prosody rate="${rate}" pitch="${pitch}" volume="${volume}">
      ${sanitizedStyle ? `<mstts:express-as style="${sanitizedStyle}" styledegree="1">` : ''}
      ${addPausingAndEmphasis(request.text)}
      ${sanitizedStyle ? '</mstts:express-as>' : ''}
    </prosody>
  </voice>
</speak>`.trim();

      synthesizer.speakSsmlAsync(
        ssml,
        result => {
          clearTimeout(timeoutId);
          synthesizer.close();
          
          if (result.errorDetails) {
            reject(new Error(result.errorDetails));
            return;
          }

          try {
            const { audioData } = result;
            if (!audioData || audioData.byteLength === 0) {
              reject(new Error('No audio data received'));
              return;
            }

            // Process audio data in chunks to avoid "Maximum call stack size exceeded" error
            const audioArray = new Uint8Array(audioData);
            let binary = '';
            const chunkSize = 1024; // Process in smaller chunks to avoid call stack issues
            
            // Process the array in chunks to avoid the "too many arguments" error
            for (let i = 0; i < audioArray.length; i += chunkSize) {
              const chunk = audioArray.slice(i, i + chunkSize);
              // Convert Uint8Array to regular array for String.fromCharCode
              const chunkArray = Array.from(chunk);
              binary += String.fromCharCode.apply(null, chunkArray);
            }
            
            const base64String = btoa(binary);
            const audioResult = `data:audio/mp3;base64,${base64String}`;
            
            // Store in cache for future use
            if (audioCache.size >= CACHE_MAX_SIZE) {
              // Remove oldest entry if cache is full
              const oldestKey = audioCache.keys().next().value;
              audioCache.delete(oldestKey);
            }
            audioCache.set(cacheKey, audioResult);
            
            resolve(audioResult);
          } catch (error: any) {
            reject(new Error('Failed to process audio data: ' + error.message));
          }
        },
        error => {
          clearTimeout(timeoutId);
          synthesizer.close();
          reject(new Error('Speech synthesis failed: ' + error));
        }
      );
    } catch (error: any) {
      console.error('Azure speech synthesis failed:', serializeError(error));
      reject(new Error('Failed to initialize speech synthesis: ' + error.message));
    }
  });
};